import React, { useCallback, useEffect, useState } from 'react';
import { driverApi, DriverDocument, DriverMe } from '../../services/driverApi';
import { resolveEgovLaunchHref } from '../../utils/egovLaunch';
import { DriverCopy, formatMoney } from './driverI18n';
import { avrPeriodLabel, docAmount, docTitle, isSignable, statusColor } from './driverDoc';
import { DriverHeader } from './DriverHeader';

interface Props {
  t: DriverCopy;
  id: string;
  billing?: DriverMe['billing'];
  onBack: () => void;
  onPay?: () => void;
}

export const DriverDocumentPage: React.FC<Props> = ({ t, id, billing, onBack, onPay }) => {
  const [doc, setDoc] = useState<DriverDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signing, setSigning] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      setDoc(await driverApi.getDocument(id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Документ не найден');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (!id || !doc) {
      setPdfUrl(null);
      return;
    }
    let revoked = false;
    let objectUrl: string | null = null;
    setPdfLoading(true);
    setPdfError('');
    void driverApi
      .fetchPdfBlob(id)
      .then((blob) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      })
      .catch((e) => {
        if (revoked) return;
        setPdfUrl(null);
        setPdfError(e instanceof Error ? e.message : t.pdfMissing);
      })
      .finally(() => {
        if (!revoked) setPdfLoading(false);
      });
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, doc, t.pdfMissing]);

  async function download() {
    if (!id || !doc) return;
    setDownloading(true);
    try {
      const blob = await driverApi.fetchPdfBlob(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${docTitle(doc).replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.pdfMissing);
    } finally {
      setDownloading(false);
    }
  }

  async function sign() {
    if (signBlocked) {
      setError(billing?.message || t.payBeforeSign);
      onPay?.();
      return;
    }
    setSigning(true);
    setError('');
    try {
      const session = await driverApi.createSignSession([id]);
      const href = resolveEgovLaunchHref(session.eGovMobileLaunchLink);
      if (!href) throw new Error('Не получена ссылка eGov Mobile');
      window.location.assign(href);
    } catch (e) {
      setSigning(false);
      setError(e instanceof Error ? e.message : 'Ошибка подписания');
    }
  }

  const title = doc ? docTitle(doc) : t.document;
  const canSign = doc ? isSignable(doc) : false;
  const signBlocked = Boolean(
    ((billing?.blocked && billing?.payer === 'driver') || doc?.billing?.blocked) &&
      doc?.source !== 'park'
  );
  const period = doc ? avrPeriodLabel(doc) : '';
  const amount = doc ? docAmount(doc) : null;

  return (
    <div className="app-container driver-cabinet">
      <DriverHeader t={t} title={title} onBack={onBack} />
      <main className="main-content driver-main">
        {loading && !doc ? (
          <div className="no-documents">{t.loading}</div>
        ) : !doc ? (
          <div className="no-documents">{error || 'Документ не найден'}</div>
        ) : (
          <>
            {error && <div className="driver-error">{error}</div>}
            {signBlocked && (
              <div className="driver-error">{billing?.message || t.payBeforeSign}</div>
            )}
            <div className="driver-summary">
              <div className="driver-summary-row">
                <span>{t.status}</span>
                <strong>
                  <span className="driver-doc-status" style={{ background: statusColor(doc) }}>
                    {doc.statusLabel || doc.status}
                  </span>
                </strong>
              </div>
              <div className="driver-summary-row">
                <span>{t.source}</span>
                <strong>{doc.source === 'park' ? t.park : t.yandex}</strong>
              </div>
              <div className="driver-summary-row">
                <span>{t.buyer}</span>
                <strong>{doc.buyerName || '—'}</strong>
              </div>
              <div className="driver-summary-row">
                <span>{t.seller}</span>
                <strong>{doc.sellerName || doc.fio || '—'}</strong>
              </div>
              {period && (
                <div className="driver-summary-row">
                  <span>{t.period}</span>
                  <strong>{period}</strong>
                </div>
              )}
              {amount != null && (
                <div className="driver-summary-row total">
                  <span>{t.amount}</span>
                  <strong>{formatMoney(amount)} ₸</strong>
                </div>
              )}
            </div>

            <div className="driver-doc-toolbar">
              <button
                type="button"
                className="btn-reset"
                disabled={downloading || pdfLoading || (!pdfUrl && !doc.hasPdf)}
                onClick={() => void download()}
              >
                {downloading ? t.downloading : t.download}
              </button>
              {canSign && (
                <button
                  type="button"
                  className="btn-apply"
                  disabled={signing || signBlocked}
                  onClick={() => (signBlocked ? onPay?.() : void sign())}
                >
                  {signBlocked ? t.goPay : signing ? t.goingEgov : t.signOne}
                </button>
              )}
            </div>

            <div className="driver-pdf-viewer">
              {pdfLoading && <div className="no-documents">{t.pdfLoading}</div>}
              {!pdfLoading && pdfError && <div className="driver-error">{pdfError}</div>}
              {!pdfLoading && !pdfError && !pdfUrl && <div className="no-documents">{t.pdfMissing}</div>}
              {pdfUrl && <iframe title={title} src={pdfUrl} className="driver-pdf-frame" />}
            </div>
          </>
        )}
      </main>
    </div>
  );
};
